import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useUI } from '@/hooks/ui';
import { useTranslation } from '@/i18n';
import { Card } from '@/components/ui/cards/Card';
import { IconScout } from '@/components/ui/images/IconScout';
import { colors, zIndex } from '@/assets/styles';
import { Text } from '@/components/ui/text/Text';
import { Spinner } from '@/components/ui/loading/Spinner';
import RegisterAttendanceForm from '@/components/features/attendance/myAttendance/RegisterAttendanceForm';
import RegisterIncidentForm from '@/components/features/attendance/myAttendance/RegisterIncidentForm';
import DetailsAttendanceForm from '@/components/features/attendance/myAttendance/DetailsAttendanceForm';
import ShiftRequestForm from '@/components/features/attendance/myAttendance/ShiftRequestForm';
import ManagedAttendanceDetail from '@/components/features/attendance/myAttendance/ManagedAttendanceDetail';
import IncidentDetailForm from '@/components/features/attendance/myAttendance/IncidentDetailForm';
import AbsenceDetailForm from '@/components/features/absence/AbsenceDetailForm';
import CreateMedicalLeaveForm from '@/components/features/absence/CreateMedicalLeaveForm';
import CreateSpecialLeaveForm from '@/components/features/absence/CreateSpecialLeaveForm';
import ManualAttendanceForm from '@/components/features/attendance/myAttendance/ManualAttendanceForm';
import { RoleFormPanel } from '@/components/features/authorization/RoleFormPanel';
import { ProgressivityFormPanel } from '@/components/features/attendance/configuration/ProgressivityFormPanel';
import { AreaFormPanel } from '@/components/features/organization/AreaFormPanel';
import { PositionFormPanel } from '@/components/features/organization/PositionFormPanel';
import { CostCenterFormPanel } from '@/components/features/organization/CostCenterFormPanel';
import { FractionalLeaveFormPanel, FractionalLeaveCreatePanel } from '@/components/features/fractionalLeave';
import { WorkingDayFormPanel } from '@/components/features/shift/WorkingDayFormPanel';
import { AssignScheduleFormPanel } from '@/components/features/shift/AssignScheduleFormPanel';
import { CreateHolidayFormPanel } from '@/components/features/attendance/holiday/CreateHolidayFormPanel';
import { EditHolidayFormPanel } from '@/components/features/attendance/holiday/EditHolidayFormPanel';
import { HolidayByRegionFormPanel } from '@/components/features/attendance/holiday/HolidayByRegionFormPanel';
import { ConceptDetailPanel } from '@/components/features/attendance/concept/ConceptDetailPanel';
import { ConceptCreatePanel } from '@/components/features/attendance/concept/ConceptCreatePanel';
import { RecalculoDetailPanel } from '@/components/features/attendance/recalculation/RecalculoDetailPanel';
import { ShiftChangeRequestDetailPanel } from '@/components/features/attendance/shiftChangeRequests/ShiftChangeRequestDetailPanel';
import { MarkDetailPanel } from '@/components/features/attendance/marksReport/MarkDetailPanel';
import { AdjustMarkPanel } from '@/components/features/attendance/marksReport/AdjustMarkPanel';
import { LocationFormPanel } from '@/components/features/checkInLocation';
import {
  CreateSecurityGroupingFormPanel,
  EditSecurityGroupingFormPanel,
  AssignedEmployeeDetailPanel,
} from '@/components/features/securityGrouping';
import { DeviceDetailPanel } from '@/components/features/device';


// Component registry for side panel dynamic rendering.
// Double-cast is intentional: components have specific props but are invoked
// at runtime with the correct props via sidePanel.props — enforced by callers.
type SidePanelComponent = React.ComponentType<Record<string, unknown>>;

const componentRegistry: Record<string, SidePanelComponent> = {
  RegisterAttendanceForm,
  RegisterIncidentForm,
  DetailsAttendanceForm,
  ShiftRequestForm,
  ManagedAttendanceDetail,
  IncidentDetailForm,
  AbsenceDetailForm,
  CreateMedicalLeaveForm,
  CreateSpecialLeaveForm,
  ManualAttendanceForm,
  RoleFormPanel,
  ProgressivityFormPanel,
  AreaFormPanel,
  PositionFormPanel,
  CostCenterFormPanel,
  FractionalLeaveFormPanel,
  CreateSecurityGroupingFormPanel,
  EditSecurityGroupingFormPanel,
  AssignedEmployeeDetailPanel,
  FractionalLeaveCreatePanel,
  WorkingDayFormPanel,
  AssignScheduleFormPanel,
  CreateHolidayFormPanel,
  EditHolidayFormPanel,
  HolidayByRegionFormPanel,
  ConceptDetailPanel,
  ConceptCreatePanel,
  LocationFormPanel,
  RecalculoDetailPanel,
  ShiftChangeRequestDetailPanel,
  MarkDetailPanel,
  AdjustMarkPanel,
  DeviceDetailPanel,
} as unknown as Record<string, SidePanelComponent>;

 
export function registerSidePanelComponent(
  componentKey: string,
  Component: SidePanelComponent,
): void {
  componentRegistry[componentKey] = Component;
}

export function clearSidePanelRegistry(): void {
  for (const registryKey of Object.keys(componentRegistry)) {
    delete componentRegistry[registryKey];
  }
}

const SidePanelContainer: React.FC = () => {
  const { sidePanel, closeSidePanel } = useUI();
  const { t: translate } = useTranslation();
  const isOpen = !!sidePanel?.open;
  const isLoading = !!sidePanel?.isLoading;
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const timeOut = Number(import.meta.env.VITE_API_TIMEOUT) || 10000;
  const handleClose = useCallback(() => {
    closeSidePanel();
  }, [closeSidePanel]);

  // Timeout management: 10s timeout for loading state
  useEffect(() => {
    if (isOpen && isLoading) {
      setLoadingTimedOut(false);
      timeoutRef.current = window.setTimeout(() => {
        setLoadingTimedOut(true);
      }, timeOut);
    } else {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setLoadingTimedOut(false);
    }

    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isOpen, isLoading, timeOut]);

  if (!isOpen) return null;

  const DynamicComponent = sidePanel?.component ? componentRegistry[sidePanel.component] : null;

  return (
    <>
      {/* Overlay */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: colors.grays.overlayBackdrop,
          zIndex: zIndex.sidePanelOverlay,
        }}
        data-testid="side-panel-overlay"
        onClick={handleClose}
      />
      {/* Panel */}
      <Card
        padding="0"
        borderRadius="0px"
        className="fixed top-0 right-0 h-screen w-full max-w-[370px] flex flex-col shadow-xl"
        style={{ zIndex: zIndex.sidePanel }}
        data-testid="side-panel"
      >
        <div className="flex items-center justify-between pt-6 px-6">
          <Text
            variant="body-title-medium"
            color={colors.primary.dash}
          >
            {sidePanel?.title || translate('common:sidepanel.defaultTitle')}
          </Text>
          <button
            type="button"
            onClick={handleClose}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            aria-label={translate('common:actions.close')}
          >
            <IconScout name="close" color={colors.important.main} size="lg" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 relative">
          {/* Loading spinner overlay */}
          {isLoading && !loadingTimedOut && (
            <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
              <Spinner size="lg" color="primary" />
            </div>
          )}
          {/* Timeout error */}
          {loadingTimedOut && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-10 gap-4">
              <IconScout name="alert" color={colors.important.main} size="lg" />
              <Text variant="body-medium" color={colors.important.main}>
                {translate('common:sidepanel.loadError')}
              </Text>
              <Text variant="small-regular" color={colors.grays.neutral66}>
                {translate('common:sidepanel.loadErrorHint')}
              </Text>
            </div>
          )}
          {/* Always render component so hooks run and load data */}
          {DynamicComponent ? (
            <DynamicComponent {...(sidePanel?.props || {})} />
          ) : (
            <p className="text-sm" style={{ color: colors.grays.neutral66 }}>{translate('common:sidepanel.notAvailable')}</p>
          )}
        </div>
      </Card>
    </>
  );
};

export default SidePanelContainer;
